import {
  isBoolean,
  isFunction,
  isNumber,
  isObjectLike,
  isString,
} from './guards';
import { hashInstructions } from './hash';
import { applyProps } from './props';
import type {
  BoundaryNode,
  Branch,
  Child,
  ChildReader,
  ClientNode,
  DeferNode,
  Dynamic,
  EachNode,
  ElNode,
  FragmentNode,
  Gateable,
  IfNode,
  Instruction,
  IslandSpec,
  Props,
  RawNode,
  Reader,
  RegionNode,
  SafeHtml,
  ServerNode,
  SigNode,
  StaticNode,
  SigValue,
  Template,
  TemplateHole,
  TemplateSlot,
  TextNode,
  TplNode,
} from './types';
import {
  childReader,
  InstructionError,
  isInstruction,
  normaliseChildren,
  toReader,
} from './values';

function toList(children: readonly Child[] | Child): readonly Child[] {
  return Array.isArray(children) ? children : [children];
}

export function el(
  tag: string,
  props?: Props | null,
  ...children: readonly Child[]
): ElNode {
  if (!tag) throw new InstructionError('el() needs a tag name');
  const applied = applyProps(props);
  return {
    t: 'el',
    tag,
    attrs: applied.attrs,
    dyn: applied.dyn,
    on: applied.on,
    children: normaliseChildren(children),
    gate: applied.gate,
    key: applied.key,
    ref: applied.ref,
  };
}

export function text(value: string): TextNode {
  return { t: 'text', value };
}

export function sig(read: Dynamic<SigValue> | ChildReader): SigNode {
  return {
    t: 'sig',
    read: childReader(isFunction(read) ? read : () => read.get()),
  };
}

/** A plain value is accepted where a dynamic one is expected; it reads as a constant. */
function readerOf<T>(
  value: T | Dynamic<T>,
  isPlain: (value: T | Dynamic<T>) => value is T,
): Reader<T> {
  if (isPlain(value)) return () => value;
  return toReader(value);
}

const isList = <T>(
  value: readonly T[] | Dynamic<readonly T[]>,
): value is readonly T[] => Array.isArray(value);

/** A branch given as a function is built when it is shown; anything else is built now. */
function branchThunk(branch: Branch | undefined): () => readonly Instruction[] {
  if (branch === undefined) return () => [];
  // A zero-argument function here is the branch itself, not a text reader: a
  // reader is only a reader inside `sig`, and `when` is given its sides.
  if (isFunction(branch)) return () => normaliseChildren(toList(branch()));
  const built = normaliseChildren(toList(branch));
  return () => built;
}

/**
 * A condition reads as JavaScript's `if` does: any truthy value shows the body.
 * A static condition is decided now; a dynamic one is read through `Boolean`, so
 * the node's condition is always a boolean however the template wrote it.
 */
export function when(
  cond: boolean | Dynamic<unknown>,
  body: Branch,
  otherwise?: Branch,
): IfNode {
  const read = isBoolean(cond) ? () => cond : toReader(cond);
  return {
    t: 'if',
    cond: () => Boolean(read()),
    body: branchThunk(body),
    otherwise: branchThunk(otherwise),
  };
}

export function each<T>(
  items: readonly T[] | Dynamic<readonly T[]>,
  key: (item: T, index: number) => string,
  row: (item: T, index: Reader<number>, read: Reader<T>) => Child,
  options?: { readonly indexed?: boolean },
): EachNode<T> {
  return {
    t: 'each',
    items: readerOf(items, isList),
    key,
    indexed: options?.indexed ?? row.length >= 2,
    tracksItem: row.length >= 3,
    build: row,
    row: (item, index, read) => {
      const built = row(item, index, read);
      // A row is almost always one instruction; the general walk is for the rest.
      return isInstruction(built) ? [built] : normaliseChildren([built]);
    },
  };
}

/** Brands sanitised HTML. Only a sanitiser should call this. */
export function safeHtml(html: string): SafeHtml {
  return { __safeHtml: true, html };
}

export function raw(safe: SafeHtml): RawNode {
  if (!isObjectLike(safe) || safe.__safeHtml !== true) {
    throw new InstructionError(
      'raw() accepts SafeHtml only; run the text through the sanitiser first',
    );
  }
  return { t: 'raw', html: safe.html };
}

/**
 * Elements a parent indexes, so a clone of them is not the same as building them.
 *
 * `select.options`, `table.rows` and their kin are collections the parent maintains; a
 * subtree carrying one is built rather than cloned, and hoisting it silently gave a
 * select with no options.
 */
const INDEXED_BY_PARENT = new Set([
  'option',
  'optgroup',
  'select',
  'datalist',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
]);

function holdsIndexedElement(nodes: readonly Instruction[]): boolean {
  return nodes.some((node) => {
    if (node.t === 'el')
      return (
        INDEXED_BY_PARENT.has(node.tag) || holdsIndexedElement(node.children)
      );
    if (node.t === 'static' || node.t === 'fragment')
      return holdsIndexedElement(node.children);
    return false;
  });
}

/**
 * A constant subtree: hashed once, cloned on the client, cached as a string on the server.
 *
 * A subtree holding an element its parent indexes is returned as a fragment instead, so
 * it is built in place.
 */
export function stat(...children: readonly Child[]): StaticNode | FragmentNode {
  const normalised = normaliseChildren(children);
  if (holdsIndexedElement(normalised))
    return { t: 'fragment', children: normalised };
  return {
    t: 'static',
    hash: hashInstructions(normalised),
    children: normalised,
  };
}

export function defer(
  pending: readonly Child[] | Child,
  resolve: () => Promise<Child>,
): DeferNode {
  return {
    t: 'defer',
    pending: normaliseChildren(toList(pending)),
    resolve: async () => normaliseChildren([await resolve()]),
  };
}

export function boundary(
  children: readonly Child[] | Child,
  fallback: (cause: unknown) => Child,
): BoundaryNode {
  return {
    t: 'boundary',
    children: normaliseChildren(toList(children)),
    fallback: (cause) => normaliseChildren([fallback(cause)]),
  };
}

const REGION_MODES = new Set(['client', 'server', 'hybrid']);
const REGION_SOURCES = new Set(['fragment', 'federated']);

export function region(spec: Omit<RegionNode, 't'>): RegionNode {
  if (!REGION_MODES.has(spec.mode))
    throw new InstructionError(`Unknown region mode "${spec.mode}"`);
  if (!REGION_SOURCES.has(spec.source))
    throw new InstructionError(`Unknown region source "${spec.source}"`);
  if (!spec.name || !spec.expose)
    throw new InstructionError('A region needs a name and an expose');
  return { t: 'region', ...spec };
}

export function gate<N extends Gateable>(
  action: string,
  subject: string,
  node: N,
): N {
  return { ...node, gate: { action, subject } };
}

export function gateField<N extends Gateable>(
  action: string,
  subject: string,
  field: string,
  node: N,
): N {
  return { ...node, gate: { action, subject, field } };
}

export function island(spec: IslandSpec, node: ElNode): ElNode {
  if (!spec.component)
    throw new InstructionError('An island needs a component key');
  return { ...node, island: spec };
}

export function fragment(...children: readonly Child[]): FragmentNode {
  return { t: 'fragment', children: normaliseChildren(children) };
}

/** Content the browser renders after hydration; the server writes `fallback` in its place. */
export function client(
  body: (() => Child) | null,
  fallback?: Child,
): ClientNode {
  return {
    t: 'client',
    body,
    fallback: fallback === undefined ? [] : normaliseChildren([fallback]),
  };
}

/** Content only the server renders; `null` in a browser build, where it is adopted as written. */
export function server(children: Child | null): ServerNode {
  return {
    t: 'server',
    children: children === null ? null : normaliseChildren([children]),
  };
}

/**
 * A compiled template: the compiler hoists one per markup subtree to module scope.
 *
 * The parts are markup the compiler wrote and escaped from source; they are
 * parsed as HTML, so they must never be built from data at run time.
 */
export function template(
  parts: readonly string[],
  slots: readonly TemplateSlot[],
): Template {
  if (parts.length !== slots.length + 1)
    throw new InstructionError(
      'A template has one more part than it has slots',
    );
  const tag = /^<([A-Za-z][A-Za-z0-9-]*)/.exec(parts[0] ?? '')?.[1];
  if (!tag)
    throw new InstructionError('A template starts with its root element');
  return { tag, parts, slots };
}

/** A content slot's value as children; a reader and plain text, by far the commonest, skip the general walk. */
export function holeChildren(hole: Child): readonly Instruction[] {
  if (isFunction(hole) && hole.length === 0)
    return [{ t: 'sig', read: childReader(hole) }];
  if (isString(hole)) return hole === '' ? [] : [{ t: 'text', value: hole }];
  if (isNumber(hole)) return [{ t: 'text', value: String(hole) }];
  return normaliseChildren([hole]);
}

/**
 * Whether a content slot's value is a reader. The compiler marks the readers it
 * wrote; anything else is a reader when it is a function with no parameters.
 * Asking a new closure its arity costs more than reading it, once per row.
 */
export function holeIsReader(
  slot: TemplateSlot,
  hole: TemplateHole,
): hole is Reader<SigValue> | ChildReader {
  return slot.read === true || (isFunction(hole) && hole.length === 0);
}

/** One use of a template: the values as given; a content value becomes children where it is rendered. */
export function tpl(
  compiled: Template,
  holes: readonly TemplateHole[],
): TplNode {
  if (holes.length !== compiled.slots.length)
    throw new InstructionError('A template takes one value per slot');
  return { t: 'tpl', template: compiled, holes };
}
